import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  selectCacheSessionId,
  selectCurrentReportId,
  selectSessionId,
} from "../../../../store/selectors/currentReportSelectors.ts";
import { selectLogQueries } from "../../../../store/selectors/devToolsSelectors";
import {
  selectConditions,
  selectReportType,
  selectSorts,
  selectTabularFields,
  selectTabularGrouping,
  selectTabularSettingsState,
} from "../../../../store/selectors/fieldsStateSelectors.ts";
import { createTabularDataRequest } from "../utilities/createTabularDataRequest.ts";
import type { BuildTabularReportRequest } from "../../../../../../../../api/types/svTypes.ts";

const MaximumExportRecordsCount = 10000;

export default function useRequestConfigBuilder() {
  const groupAreaValues = useSelector(selectTabularGrouping);
  const fieldsAreaValues = useSelector(selectTabularFields);
  const sortingAreaValues = useSelector(selectSorts);
  const conditionsAreaValues = useSelector(selectConditions);
  const settingsAreaValues = useSelector(selectTabularSettingsState);
  const reportType = useSelector(selectReportType);

  const reportId = useSelector(selectCurrentReportId);
  const sessionId = useSelector(selectSessionId);
  const cacheSessionId = useSelector(selectCacheSessionId);
  const isQueryLoggingActive = useSelector(selectLogQueries);

  const sorting = useMemo(() => sortingAreaValues.map((sf) => sf.config), [sortingAreaValues]);

  const requestConfig = useMemo(() => {
    const request: BuildTabularReportRequest = {
      ...createTabularDataRequest(
        conditionsAreaValues,
        fieldsAreaValues,
        sorting,
        groupAreaValues,
        settingsAreaValues,
        reportType
      ),
      sessionId,
      cacheSessionId,
      withDrilldown: true,
      reportId: reportId ?? "",
      calcTotalCount: false,
      calcGroupTotals: true,
      take: MaximumExportRecordsCount,
      skip: 0,
      useQueryLogging: isQueryLoggingActive === true,
    };
    return request;
  }, [
    sorting,
    conditionsAreaValues,
    fieldsAreaValues,
    groupAreaValues,
    settingsAreaValues,
    reportType,
    sessionId,
    cacheSessionId,
    reportId,
    isQueryLoggingActive,
  ]);

  return { requestConfig };
}
